import { useState, useEffect } from "react";
import { Bell, Menu, Search, MessageSquare, BellOff } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuthStore } from "@/store/authStore";
import { MobileNavDrawer } from "./MobileNavDrawer";
import { useBrandStore } from "@/store/brandStore";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useChatStore } from "@/store/chat";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { isNotificationSupported } from "@/utils/platformDetection";

// Add this component to show notification status
function NotificationStatusIndicator() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [notificationStatus, setNotificationStatus] = useState<{
    isSupported: boolean;
    isSubscribed: boolean;
    permission: NotificationPermission | 'unsupported';
  }>({
    isSupported: isNotificationSupported(),
    isSubscribed: !!user?.fcm_token,
    permission: isNotificationSupported() ? Notification.permission : 'unsupported'
  });

  useEffect(() => {
    const checkStatus = () => {
      setNotificationStatus({
        isSupported: isNotificationSupported(),
        isSubscribed: !!user?.fcm_token,
        permission: isNotificationSupported() ? Notification.permission : 'unsupported'
      });
    };

    checkStatus();
    // Check status every 5 seconds
    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, [user?.fcm_token]);

  // Don't render anything on native platforms
  if (!notificationStatus.isSupported) {
    return null;
  }

  if (notificationStatus.permission === 'denied') {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-red-600 hover:text-red-700"
        title={t('notificationPermissionDenied')}
      >
        <BellOff className="h-4 w-4" />
      </Button>
    );
  }

  if (!notificationStatus.isSubscribed) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-yellow-600 hover:text-yellow-700"
        title={t('enableWebPushNotifications')}
      >
        <Bell className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className="text-green-600 hover:text-green-700"
      title={t('webPushNotificationsEnabled')}
    >
      <Bell className="h-4 w-4" />
    </Button>
  );
}

export function DashboardHeader() {
  const [showSearch, setShowSearch] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const { user } = useAuthStore();
  const { logo, name } = useBrandStore();
  const { t } = useTranslation();
  const { unreadCount } = useChatStore();
  const isMobile = useIsMobile();

  return (
    <header className="border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-30 w-full">
      <div className="px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setShowMobileNav(true)}
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">{t("menu")}</span>
          </Button>

          {!showSearch ? (
            <Button
              variant="ghost"
              size="icon"
              className="sm:hidden"
              onClick={() => setShowSearch(true)}
            >
              <Search className="h-5 w-5" />
              <span className="sr-only">{t("search")}</span>
            </Button>
          ) : (
            <div className="fixed inset-0 z-50 bg-background sm:hidden animate-fade-in">
              <div className="flex h-16 items-center gap-2 px-4">
                <div className="flex-1">
                  <Input
                    type="search"
                    placeholder={t("search")}
                    className="w-full pl-8"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSearch(false)}
                >
                  {t("cancel")}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          <ThemeToggle />
          <NotificationStatusIndicator />

          {!isMobile && (
            <Link to="/dashboard/chat">
              <Button variant="ghost" size="icon" className="relative">
                <MessageSquare className="h-5 w-5" />
                <span className="sr-only">{t("chat")}</span>
                {unreadCount > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </Badge>
                )}
              </Button>
            </Link>
          )}

          <div className="flex items-center gap-4">
            {/* Use brand logo instead of avatar if available */}
            <Avatar>
              {logo ? (
                <AvatarImage
                  src={logo}
                  alt={name || t("brandLogo")}
                  className="object-cover"
                  onError={(e) => {
                    console.error("Error loading logo:", e);
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <AvatarImage
                  src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${
                    user?.name || "demo"
                  }`}
                />
              )}
              <AvatarFallback>
                {user?.name?.charAt(0) || t("brandFallback")}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
      </div>

      <MobileNavDrawer open={showMobileNav} onOpenChange={setShowMobileNav} />
    </header>
  );
}
